import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import api from "../../api";
import AnswerFields from "./AnswerFields";

export default function ScenarioPreview({ scenario, onClose }) {
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [width, setWidth] = useState("mobile");
  const check = async () => {
    setBusy(true);
    setError("");
    setResult(null);
    try {
      const { data } = await api({
        method: "post",
        url: "/surveys/scenario-preview",
        data: {
          scenario: {
            title: scenario.title,
            instructions: scenario.instructions || "",
            questions: scenario.questions,
          },
          answers,
        },
      });
      setResult(data);
    } catch (e) {
      const detail = e.response?.data?.detail;
      setError(
        Array.isArray(detail)
          ? detail
              .map((issue) => `${issue.loc.join(" / ")}: ${issue.msg}`)
              .join("; ")
          : typeof detail === "string"
            ? detail
            : "Preview check failed. Your sample answers are still here; try again.",
      );
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open fullWidth maxWidth="md" onClose={() => !busy && onClose()}>
      <DialogTitle>Preview scenario</DialogTitle>
      <DialogContent>
        <Alert severity="info" sx={{ mb: 2 }}>
          Sample responses only. Checking uses the same validation as
          submission; it does not save responses, publish a version or create an
          assignment.
        </Alert>
        {scenario.questions.some((q) => q.photo_required) && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            This scenario requires photos. Preview checks answers only; real
            submission also verifies stored photo evidence.
          </Alert>
        )}
        <ToggleButtonGroup
          exclusive
          value={width}
          size="small"
          aria-label="Preview width"
          onChange={(_, value) => value && setWidth(value)}
          sx={{ mb: 2 }}
        >
          <ToggleButton value="mobile">Mobile width</ToggleButton>
          <ToggleButton value="desktop">Desktop width</ToggleButton>
        </ToggleButtonGroup>
        <Box
          sx={{
            maxWidth: width === "mobile" ? 390 : "100%",
            mx: "auto",
            border: "1px solid #d4dde5",
            borderRadius: 2,
            p: 2,
          }}
        >
          <Typography variant="h6">{scenario.title}</Typography>
          <Typography variant="caption" display="block" sx={{ mb: 2 }}>
            {scenario.version
              ? `Version ${scenario.version}`
              : "Unsaved authoring draft"}{" "}
            · {scenario.questions.length} questions
          </Typography>
          <Typography sx={{ whiteSpace: "pre-wrap", mb: 3 }}>
            {scenario.instructions}
          </Typography>
          <AnswerFields
            questions={scenario.questions}
            answers={answers}
            disabled={busy}
            errors={result?.errors || []}
            onChange={(value) => {
              setAnswers(value);
              setResult(null);
              setError("");
            }}
          />
        </Box>
        <Stack spacing={1} sx={{ mt: 2 }}>
          {error && <Alert severity="error">{error}</Alert>}
          {result && (
            <Alert severity={result.valid ? "success" : "warning"}>
              {result.valid
                ? "Sample responses pass submission validation. Nothing was saved."
                : `${result.errors.length} answer checks need attention.`}
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ flexWrap: "wrap" }}>
        <Button
          disabled={busy}
          onClick={() => {
            setAnswers({});
            setResult(null);
            setError("");
          }}
        >
          Reset sample
        </Button>
        <Button disabled={busy} onClick={onClose}>
          Close preview
        </Button>
        <Button variant="contained" disabled={busy} onClick={check}>
          {busy ? "Checking…" : "Check responses"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
